import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "hesitation-popup__card" }
const _hoisted_2 = { class: "hesitation-popup__price-row" }
const _hoisted_3 = { class: "hesitation-popup__plan-name-group" }
const _hoisted_4 = { class: "hesitation-popup__price-sub" }
const _hoisted_5 = { class: "wg-no-translate" }
const _hoisted_6 = { class: "wg-no-translate" }
const _hoisted_7 = { class: "hesitation-popup__price" }
const _hoisted_8 = { class: "wg-no-translate" }
const _hoisted_9 = { class: "hesitation-popup__price-old wg-no-translate" }
const _hoisted_10 = { class: "hesitation-popup__cancel-line" }

import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { Raw } from 'vue'
import type { BillingView, PlanView } from '@/models/billing'
import type { PlanPrice } from '@/models/billing.plans'
import { AnnualPlanView } from '@/models/billing.plans'
import BasePopup from '@/components/ui/BasePopup.vue'
import BillingSubscribeButton from './BillingSubscribeButton.vue'
import TrialBadge from './TrialBadge.vue'
import { backend } from '@/services/backend'
import { emitter } from '@/services/mitt'
import { Sentry } from '@/services/sentry'


export default /*@__PURE__*/_defineComponent({
  __name: 'HesitationPopup',
  props: {
    billing: {}
  },
  setup(__props: any) {

const props = __props

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const visible = ref(false)
const subscribing = ref(false)
const annualPlan = ref<Raw<PlanView> | null>(null)

function getPrice(price: PlanPrice): string {
  if (props.billing.platform === 'android') {
    return price.withPeriod.perYear
  }
  return price.withPeriod.perMonth
}

const displayPrice = computed(() => {
  return annualPlan.value ? getPrice(annualPlan.value.price) : null
})

// The popup only shows once the hesitation discount has been applied, so
// annualPlan always has a discount by the time this is rendered.
const displayOldPrice = computed(() => {
  return annualPlan.value ? getPrice(annualPlan.value.priceNoDiscount) : null
})

// The hesitation discount is always a percentage-type coupon.
const discountPercent = computed(
  () => annualPlan.value?.discount?.value ?? null,
)

const title = computed(() => {
  return `Take an extra ${discountPercent.value}% off your first year.`
})

const ctaLabel = computed(() => {
  return `Claim ${discountPercent.value}% Discount`
})

const trialLength = computed(() => props.billing.trialLength('annual'))

const billedPerPeriod = computed(() => {
  if (!annualPlan.value) {
    return ''
  }
  return `${annualPlan.value.formatPrice(
    annualPlan.value.normalizedAmount.perPeriod,
  )}/yr`
})

const oldBilledPerPeriod = computed(() => {
  if (!annualPlan.value) {
    return null
  }
  return annualPlan.value.formatPrice(
    annualPlan.value.normalizedAmountNoDiscount.perPeriod,
  )
})

async function handleOpen(): Promise<void> {
  if (visible.value) {
    return
  }

  try {
    const response = await backend.applyHesitationDiscount()
    props.billing.update(response)
    annualPlan.value = AnnualPlanView.createView(response.billing_plans)
    visible.value = true
    popup.value?.show()
  } catch (error) {
    Sentry.captureException(error)
  }
}

function handleSubscribeClick(): void {
  // The click bubbles from BillingSubscribeButton, which opens its own
  // SubscribePopup nested inside this popup's slot. We can't call
  // popup.hide() here - BasePopup unmounts its whole slot on hide, which
  // would tear down the just-opened SubscribePopup along with it. Instead,
  // hide this popup visually only (see the --subscribing CSS below), which
  // keeps it mounted so the nested SubscribePopup survives.
  subscribing.value = true
}

onMounted(() => {
  emitter.on('hesitationPopupOpen', handleOpen)
})

onUnmounted(() => {
  emitter.off('hesitationPopupOpen', handleOpen)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BasePopup, {
    ref_key: "popup",
    ref: popup,
    class: _normalizeClass(["hesitation-popup", { 'hesitation-popup--subscribing': subscribing.value }]),
    compact: true,
    title: title.value,
    onHide: _cache[0] || (_cache[0] = ($event: any) => (_unref(emitter).emit('hesitationPopupClose')))
  }, {
    default: _withCtx(() => [
      (visible.value && annualPlan.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _cache[6] || (_cache[6] = _createElementVNode("p", { class: "hesitation-popup__subtitle" }, " Get this exclusive discount if you start your Annual Trial today. ", -1)),
            _createElementVNode("div", _hoisted_1, [
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "hesitation-popup__badge" }, "SPECIAL OFFER", -1)),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "hesitation-popup__plan-name" }, "Annual", -1)),
                    (!__props.billing.isTrialOver)
                      ? (_openBlock(), _createBlock(TrialBadge, {
                          key: 0,
                          class: "hesitation-popup__trial-badge",
                          days: trialLength.value,
                          compact: ""
                        }, null, 8, ["days"]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("p", _hoisted_4, [
                    _cache[2] || (_cache[2] = _createTextVNode(" Billed at ", -1)),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(billedPerPeriod.value), 1),
                    _cache[3] || (_cache[3] = _createTextVNode(" (normally ", -1)),
                    _createElementVNode("span", _hoisted_6, _toDisplayString(oldBilledPerPeriod.value), 1),
                    _cache[4] || (_cache[4] = _createTextVNode(") ", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, _toDisplayString(displayPrice.value), 1),
                  _createElementVNode("div", _hoisted_9, _toDisplayString(displayOldPrice.value), 1)
                ])
              ]),
              _createElementVNode("div", {
                class: "hesitation-popup__cta",
                onClick: handleSubscribeClick
              }, [
                _createVNode(BillingSubscribeButton, {
                  billing: __props.billing,
                  plan: annualPlan.value,
                  location: "hesitation_popup",
                  label: ctaLabel.value
                }, null, 8, ["billing", "plan", "label"])
              ]),
              _createElementVNode("p", _hoisted_10, " Cancel anytime before day " + _toDisplayString(trialLength.value) + ", no charge. ", 1)
            ])
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["class", "title"]))
}
}

})